// Controller modals, output-delivery projection/polling, and card components
// for the PLC controllers page. Split from plc-controllers.js.
import React, { useEffect, useMemo, useState } from "react";

import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import { getPLCControllerOutputDelivery } from "../../../api";
import chevronIcon from "../../../assets/chevron-right.svg";
import xmarkIcon from "../../../assets/xmark.svg";
import {
  PLC_CONTROLLER_LIFECYCLE_ACTIONS,
  controllerLifecycleActionAvailability,
  controllerCanEditDraft,
  controllerStateLabel,
  controllerStateTone,
  controllerTargetKey,
  formatControllerBytes,
  plcControllerLifecycleDiff,
  plcControllerLifecycleProposalExpired,
} from "./plc-controllers-model";

const timeLabel = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
};

function ControllerDraftModal({ controller, runtimes, busy, onClose, onSubmit }) {
  const editing = Boolean(controller);
  const [name, setName] = useState(controller?.name || "");
  const [description, setDescription] = useState(controller?.description || "");
  const [reason, setReason] = useState("");
  const [targetKey, setTargetKey] = useState(() => {
    if (controller?.target) return controllerTargetKey(controller.target);
    return runtimes.length > 0 ? controllerTargetKey(runtimes[0]) : "";
  });
  const runtimeByKey = useMemo(
    () => new Map(runtimes.map((runtime) => [controllerTargetKey(runtime), runtime])),
    [runtimes]
  );
  const selectedRuntime = runtimeByKey.get(targetKey);
  const valid = name.trim().length > 0 && reason.trim().length >= 3 && (editing || selectedRuntime);

  const submit = (event) => {
    event.preventDefault();
    if (!valid) return;
    if (editing) {
      onSubmit({
        expected_version: controller.version,
        name: name.trim(),
        description: description.trim(),
        reason: reason.trim(),
      });
      return;
    }
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      mode: "isolated_linux_v1",
      target: { kind: selectedRuntime.kind, runtime_id: selectedRuntime.id },
      program_ids: [],
      reason: reason.trim(),
    });
  };

  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="gwModal plcControllerModal" onSubmit={submit}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{editing ? "Edit controller draft" : "New virtual controller draft"}</div>
            <div className="gwModalSubtitle">Configuration only. This action cannot start a worker or change plant I/O.</div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} aria-label="Close">
            <img src={xmarkIcon} alt="" className="gwIcon" />
          </button>
        </div>
        <div className="gwModalBody gwFormStack">
          {!editing && (
            <div className="gwField">
              <label className="gwLabel">Target runtime</label>
              <Select
                value={targetKey}
                onChange={setTargetKey}
                options={runtimes.map((runtime) => ({
                  value: controllerTargetKey(runtime),
                  label: `${runtime.name} · ${runtime.platform}/${runtime.architecture} · ${runtime.connectivity}`,
                }))}
                placeholder="Choose a runtime"
              />
              <div className="gwHint">Isolation stays unavailable until this exact runtime passes the Linux worker support and release gates.</div>
            </div>
          )}
          <div className="gwField">
            <label className="gwLabel">Controller name</label>
            <input className="gwInput" value={name} onChange={(event) => setName(event.target.value)} maxLength={256} placeholder="Packaging controller" autoFocus />
          </div>
          <div className="gwField">
            <label className="gwLabel">Description</label>
            <textarea className="gwTextarea" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={2048} placeholder="What this controller will own after reviewed activation" />
          </div>
          <div className="gwField">
            <label className="gwLabel">Reason for change</label>
            <textarea className="gwTextarea" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="Why this configuration draft is needed" />
          </div>
          {!editing && (
            <div className="gwNotice isSoft">
              The draft starts with the bounded default CPU, memory, process, storage, IPC, and connector policy. Application and connector assignments require the separate admission/confirmation workflow and are not guessed here.
            </div>
          )}
        </div>
        <div className="gwModalFooter">
          <button type="button" className="gwGhostBtn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="gwPrimaryBtn" disabled={busy || !valid}>
            {busy ? "Saving…" : editing ? "Save stopped draft" : "Create stopped draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteControllerModal({ controller, busy, onClose, onDelete }) {
  const [reason, setReason] = useState("");
  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <form className="gwModal" onSubmit={(event) => { event.preventDefault(); if (reason.trim().length >= 3) onDelete(reason.trim()); }}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Delete {controller.name}?</div>
            <div className="gwModalSubtitle">This removes only the stopped configuration draft. It does not stop or delete a legacy PLC program.</div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} aria-label="Close"><img src={xmarkIcon} alt="" className="gwIcon" /></button>
        </div>
        <div className="gwModalBody">
          <div className="gwField">
            <label className="gwLabel">Reason for deletion</label>
            <textarea className="gwTextarea" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} autoFocus />
          </div>
        </div>
        <div className="gwModalFooter">
          <button type="button" className="gwGhostBtn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="gwDangerBtn" disabled={busy || reason.trim().length < 3}>{busy ? "Deleting…" : "Delete draft"}</button>
        </div>
      </form>
    </div>
  );
}

function ControllerEventsModal({ controller, events, loading, onClose }) {
  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="gwModal plcControllerModal">
        <div className="gwModalHeader">
          <div><div className="gwModalTitle">{controller.name} history</div><div className="gwModalSubtitle">Bounded configuration and runtime audit events. No secrets or internal tag IDs are shown.</div></div>
          <button type="button" className="gwModalClose" onClick={onClose} aria-label="Close"><img src={xmarkIcon} alt="" className="gwIcon" /></button>
        </div>
        <div className="gwModalBody">
          {loading && <div className="gwHint">Loading controller history…</div>}
          {!loading && events.length === 0 && <div className="gwEmptyState gwEmptyCompact"><div className="gwEmptyTitle">No controller events yet</div></div>}
          <div className="plcControllerEvents">
            {events.map((event, index) => (
              <div className="plcControllerEvent" key={`${event.occurred_at || "event"}-${index}`}>
                <div><strong>{controllerStateLabel(event.kind)}</strong><span>{timeLabel(event.occurred_at)}</span></div>
                <p>{event.message}</p>
                {event.actor_name && <small>Actor: {event.actor_name}</small>}
              </div>
            ))}
          </div>
        </div>
        <div className="gwModalFooter"><button type="button" className="gwGhostBtn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

function SupportEvidenceModal({ runtime, payload, loading, error, onClose }) {
  const latest = payload?.latest;
  const available = latest?.available === true && latest?.authenticated === true;
  const snapshot = available ? latest.snapshot : null;
  const result = snapshot?.result;
  const history = Array.isArray(payload?.history) ? payload.history : [];
  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="gwModal plcControllerModal plcControllerSupportModal" role="dialog" aria-modal="true" aria-label={`${runtime.name} host support evidence`}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">{runtime.name} host support</div>
            <div className="gwModalSubtitle">Read-only cached, authenticated evidence. Opening this view does not rerun a probe or change controller execution.</div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} aria-label="Close"><img src={xmarkIcon} alt="" className="gwIcon" /></button>
        </div>
        <div className="gwModalBody gwFormStack">
          {loading && <div className="gwHint">Loading authenticated support history…</div>}
          {!loading && (!available || error) && (
            <div className="gwNotice isSoft" data-gw-card="plc_controller_support_unavailable">
              <strong>Host support evidence unavailable</strong>
              <span>{error || latest?.activation_message || "No authenticated host-isolation evidence is available for this runtime."}</span>
              <span>Dedicated controller execution remains planned and non-executable.</span>
            </div>
          )}
          {!loading && available && (
            <>
              <div className="plcControllerSupportCard" data-gw-card="plc_controller_support_evidence">
                <div className="plcControllerSupportHeading">
                  <div>
                    <strong>{result?.status === "supported" ? "Host primitives supported" : "Host support blocked"}</strong>
                    <span>{result?.stale ? "Stale cached evidence" : "Fresh cached evidence"}</span>
                  </div>
                  <span className={`gwStatusPill ${latest.activation_eligible ? "isPositive" : "isWarning"}`}>
                    {latest.activation_eligible ? "Activation preflight eligible" : "Activation blocked"}
                  </span>
                </div>
                <div className="plcControllerFacts">
                  <div><span>Runtime</span><strong>{payload?.runtime?.name || runtime.name} · {payload?.runtime?.kind || runtime.kind}</strong></div>
                  <div><span>Observed</span><strong>{timeLabel(snapshot?.evidence?.observed_at)}</strong></div>
                  <div><span>Received</span><strong>{timeLabel(latest.received_at)}</strong></div>
                  <div><span>Source</span><strong>{latest.source}</strong></div>
                  <div><span>Host</span><strong>{snapshot?.evidence?.platform}/{snapshot?.evidence?.architecture} · systemd {snapshot?.evidence?.systemd_version}</strong></div>
                  <div><span>Allocatable CPU / memory</span><strong>{snapshot?.capacity?.cpu_millicores || 0} millicores · {formatControllerBytes(snapshot?.capacity?.memory_bytes)}</strong></div>
                </div>
                <div className="gwNotice isSoft">
                  <strong>{latest.activation_message}</strong>
                  <span>Eligibility means this cached evidence may enter activation preflight. It does not make isolation executable, start a worker, deploy an application, or prove plant I/O state.</span>
                </div>
                <div className="plcControllerSupportChecks">
                  {(snapshot?.evidence?.checks || []).map((check) => (
                    <div key={check.name}>
                      <span>{check.name}</span>
                      <strong className={check.passed ? "isPassed" : "isFailed"}>{check.passed ? "Passed" : "Not proven"}</strong>
                      {!check.passed && <small>Required primitive was not proven.</small>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="plcControllerSupportHistory">
                <div className="gwSectionTitle">Authenticated history</div>
                {history.map((entry, index) => (
                  <div key={`${entry.received_at || "support"}-${index}`}>
                    <div><strong>{entry.snapshot?.result?.status === "supported" ? "Supported" : "Blocked"}</strong><span>{timeLabel(entry.snapshot?.evidence?.observed_at)}</span></div>
                    <p>{entry.snapshot?.result?.stale ? "Stale evidence · activation blocked" : entry.activation_eligible ? "Fresh · activation preflight eligible" : "Fresh evidence · activation blocked"}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="gwModalFooter"><button type="button" className="gwGhostBtn" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

const wholeNumber = (value, minimum, maximum) => Number.isSafeInteger(value) && value >= minimum && value <= maximum;
const mib = (bytes) => Math.round((Number(bytes) || 0) / (1024 * 1024));

function DraftChangeModal({ controller, programs, initialProposal, busy, onClose, onPreview, onConfirm }) {
  const currentNames = useMemo(() => (controller.programs || []).map((program) => program.name), [controller.programs]);
  const [selectedNames, setSelectedNames] = useState(currentNames);
  const [reason, setReason] = useState("");
  const [proposal, setProposal] = useState(initialProposal || null);
  const policy = controller.policy || {};
  const [limits, setLimits] = useState({
    cpu_quota_millicores: policy.cpu_quota_millicores ?? 1000,
    cpu_weight: policy.cpu_weight ?? 100,
    memory_high_mib: mib(policy.memory_high_bytes || 192 * 1024 * 1024),
    memory_max_mib: mib(policy.memory_max_bytes || 256 * 1024 * 1024),
    tasks_max: policy.tasks_max ?? 128,
    open_files_max: policy.open_files_max ?? 256,
    state_max_mib: mib(policy.state_max_bytes || 96 * 1024 * 1024),
    state_max_files: policy.state_max_files ?? 8,
    state_max_inodes: policy.state_max_inodes ?? 32,
    watchdog_milliseconds: policy.watchdog_milliseconds ?? 10000,
  });
  const setLimit = (name, value) => {
    setProposal(null);
    setLimits((current) => ({ ...current, [name]: value }));
  };
  const toggleProgram = (name) => {
    setProposal(null);
    setSelectedNames((current) => current.includes(name) ? current.filter((item) => item !== name) : [...current, name]);
  };
  const numericValid =
    wholeNumber(limits.cpu_quota_millicores, 100, 8000) &&
    wholeNumber(limits.cpu_weight, 1, 10000) &&
    wholeNumber(limits.memory_high_mib, 96, 3584) &&
    wholeNumber(limits.memory_max_mib, 128, 4096) &&
    wholeNumber(limits.tasks_max, 32, 256) &&
    wholeNumber(limits.open_files_max, 64, 1024) &&
    wholeNumber(limits.state_max_mib, 16, 256) &&
    wholeNumber(limits.state_max_files, 4, 32) &&
    wholeNumber(limits.state_max_inodes, 16, 256) &&
    wholeNumber(limits.watchdog_milliseconds, 2000, 30000);
  const canPreview = numericValid && reason.trim().length >= 3;

  const preview = async () => {
    if (!canPreview) return;
    const result = await onPreview({
      expected_version: controller.version,
      program_names: selectedNames,
      resource_limits: {
        cpu_quota_millicores: limits.cpu_quota_millicores,
        cpu_weight: limits.cpu_weight,
        memory_high_bytes: limits.memory_high_mib * 1024 * 1024,
        memory_max_bytes: limits.memory_max_mib * 1024 * 1024,
        tasks_max: limits.tasks_max,
        open_files_max: limits.open_files_max,
        state_max_bytes: limits.state_max_mib * 1024 * 1024,
        state_max_files: limits.state_max_files,
        state_max_inodes: limits.state_max_inodes,
        watchdog_milliseconds: limits.watchdog_milliseconds,
      },
      reason: reason.trim(),
    });
    if (result) setProposal(result);
  };

  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="gwModal plcControllerModal plcControllerReviewModal" role="dialog" aria-modal="true" aria-label={`Review ${controller.name}`}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Review assignments and resources</div>
            <div className="gwModalSubtitle">{controller.name} · stopped configuration only</div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} aria-label="Close"><img src={xmarkIcon} alt="" className="gwIcon" /></button>
        </div>
        <div className="gwModalBody gwFormStack">
          {!proposal && (
            <>
              <div className="gwField">
                <label className="gwLabel">Published applications</label>
                <div className="plcControllerProgramPicker">
                  {programs.length === 0 && <div className="gwHint">No PLC applications are published yet.</div>}
                  {programs.map((program) => {
                    const published = Number(program.published_version) > 0;
                    return (
                      <label key={program.name} className={!published ? "isDisabled" : ""}>
                        <input type="checkbox" checked={selectedNames.includes(program.name)} disabled={!published || busy} onChange={() => toggleProgram(program.name)} />
                        <span><strong>{program.name}</strong><small>{published ? `Published version ${program.published_version}` : "Publish before assigning"}</small></span>
                      </label>
                    );
                  })}
                </div>
                <div className="gwHint">Applications are resolved by exact public name and recompiled during preview and confirmation.</div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Resource limits</label>
                <div className="plcControllerLimitGrid">
                  <label><span>CPU ceiling (millicores)</span><NudgeNumber value={limits.cpu_quota_millicores} onChange={(value) => setLimit("cpu_quota_millicores", value)} min={100} max={8000} step={100} disabled={busy} aria-label="CPU ceiling" /></label>
                  <label><span>CPU weight</span><NudgeNumber value={limits.cpu_weight} onChange={(value) => setLimit("cpu_weight", value)} min={1} max={10000} step={10} disabled={busy} aria-label="CPU weight" /></label>
                  <label><span>Memory high (MiB)</span><NudgeNumber value={limits.memory_high_mib} onChange={(value) => setLimit("memory_high_mib", value)} min={96} max={3584} step={16} disabled={busy} aria-label="Memory high" /></label>
                  <label><span>Memory maximum (MiB)</span><NudgeNumber value={limits.memory_max_mib} onChange={(value) => setLimit("memory_max_mib", value)} min={128} max={4096} step={16} disabled={busy} aria-label="Memory maximum" /></label>
                  <label><span>Tasks maximum</span><NudgeNumber value={limits.tasks_max} onChange={(value) => setLimit("tasks_max", value)} min={32} max={256} step={8} disabled={busy} aria-label="Tasks maximum" /></label>
                  <label><span>Open files maximum</span><NudgeNumber value={limits.open_files_max} onChange={(value) => setLimit("open_files_max", value)} min={64} max={1024} step={32} disabled={busy} aria-label="Open files maximum" /></label>
                  <label><span>State maximum (MiB)</span><NudgeNumber value={limits.state_max_mib} onChange={(value) => setLimit("state_max_mib", value)} min={16} max={256} step={8} disabled={busy} aria-label="State maximum" /></label>
                  <label><span>State files maximum</span><NudgeNumber value={limits.state_max_files} onChange={(value) => setLimit("state_max_files", value)} min={4} max={32} step={1} disabled={busy} aria-label="State files maximum" /></label>
                  <label><span>State inodes maximum</span><NudgeNumber value={limits.state_max_inodes} onChange={(value) => setLimit("state_max_inodes", value)} min={16} max={256} step={4} disabled={busy} aria-label="State inodes maximum" /></label>
                  <label><span>Process watchdog (ms)</span><NudgeNumber value={limits.watchdog_milliseconds} onChange={(value) => setLimit("watchdog_milliseconds", value)} min={2000} max={30000} step={1000} disabled={busy} aria-label="Process watchdog" /></label>
                </div>
                <div className="gwHint">Preview rejects unsafe cross-field combinations and any workload that exceeds these limits. Legacy automatic task watchdogs commonly require 10,000 ms here.</div>
              </div>
              <div className="gwField">
                <label className="gwLabel">Reason for reviewed change</label>
                <textarea className="gwTextarea" value={reason} onChange={(event) => { setProposal(null); setReason(event.target.value); }} maxLength={500} placeholder="Why these exact applications and limits belong together" />
              </div>
            </>
          )}
          {proposal && (
            <div className="plcControllerProposal" data-gw-card="plc_controller_draft_proposal">
              <div className="gwNotice isSoft">
                <strong>Logical admission passed</strong>
                <span>{proposal.logical_admission?.message}</span>
                <span>No controller will be started, stopped, staged, deployed, or connected to plant I/O.</span>
              </div>
              <div className="plcControllerProposalFacts">
                <div><span>Controller</span><strong>{proposal.controller?.name || controller.name}</strong></div>
                <div><span>Target</span><strong>{proposal.target?.name || controller.target?.name}</strong></div>
                <div><span>Expires</span><strong>{timeLabel(proposal.confirmation?.expires_at)}</strong></div>
                <div><span>Proposal digest</span><code>{proposal.confirmation?.proposal_sha256}</code></div>
                <div><span>Reason</span><strong>{proposal.reason}</strong></div>
              </div>
              <div className="plcControllerProposalChanges">
                {(proposal.changes || []).map((change) => <div key={`${change.field}-${change.before}-${change.after}`}><strong>{change.field}</strong><span className="plcChangeFlow"><span>{change.before}</span><img src={chevronIcon} alt="" aria-hidden="true" className="gwIcon" /><span>{change.after}</span></span></div>)}
              </div>
              <div className="plcControllerAssignments">
                <div><span>Applications</span><strong>{proposal.applications?.length ? proposal.applications.map((program) => `${program.name} v${program.published_version}`).join(", ") : "None assigned"}</strong></div>
                <div><span>CPU / memory</span><strong>{proposal.policy?.cpu_quota_millicores} millicores · {formatControllerBytes(proposal.policy?.memory_max_bytes)}</strong></div>
              </div>
              {(proposal.warnings || []).map((warning) => <div className="gwHint" key={warning}>{warning}</div>)}
            </div>
          )}
        </div>
        <div className="gwModalFooter">
          <button type="button" className="gwGhostBtn" onClick={proposal ? () => setProposal(null) : onClose} disabled={busy}>{proposal ? "Back" : "Cancel"}</button>
          {!proposal && <button type="button" className="gwPrimaryBtn" onClick={preview} disabled={busy || !canPreview}>{busy ? "Checking…" : "Preview exact change"}</button>}
          {proposal && <button type="button" className="gwPrimaryBtn" onClick={() => onConfirm(proposal)} disabled={busy || !proposal.can_confirm}>{busy ? "Confirming…" : "Confirm stopped draft"}</button>}
        </div>
      </div>
    </div>
  );
}

const lifecycleActionLabel = (action) => `${String(action || "").slice(0, 1).toUpperCase()}${String(action || "").slice(1)}`;

function ControllerLifecycleModal({
  controller,
  action,
  initialReason = "",
  initialPreview = null,
  busy,
  onClose,
  onPreview,
  onConfirm,
}) {
  const [reason, setReason] = useState(initialReason);
  const [previewEnvelope, setPreviewEnvelope] = useState(initialPreview);
  const [retainedDisposition, setRetainedDisposition] = useState(
    initialPreview?.proposal?.retained_disposition || "preserve"
  );
  const [forceDisposition, setForceDisposition] = useState(
    initialPreview?.proposal?.force_disposition || "reject_if_present"
  );
  const [retainedResetAcknowledged, setRetainedResetAcknowledged] = useState(false);
  const [forceClearAcknowledged, setForceClearAcknowledged] = useState(false);
  const [reviewClock, setReviewClock] = useState(() => Date.now());
  const proposal = previewEnvelope?.proposal || null;
  const label = lifecycleActionLabel(action);
  const expired = proposal ? plcControllerLifecycleProposalExpired(proposal, reviewClock) : false;
  const diff = proposal ? plcControllerLifecycleDiff(controller, proposal) : [];
  const canPreview = reason.trim().length >= 3;
  const retainedResetRequiresAcknowledgement = proposal?.retained_disposition === "reset";
  const forceClearRequiresAcknowledgement = proposal?.force_disposition === "clear";

  useEffect(() => {
    if (!proposal) return undefined;
    const expiresAt = Date.parse(proposal.expires_at);
    const remaining = expiresAt - Date.now();
    if (Number.isNaN(expiresAt) || remaining <= 0) return undefined;
    const timeout = window.setTimeout(
      () => setReviewClock(Date.now()),
      Math.min(remaining + 25, 60000)
    );
    return () => window.clearTimeout(timeout);
  }, [proposal, reviewClock]);

  const preview = async () => {
    if (!canPreview) return;
    const next = await onPreview(
      action,
      reason.trim(),
      action === "restart" ? retainedDisposition : "",
      action === "restart" ? forceDisposition : ""
    );
    if (next) {
      setRetainedResetAcknowledged(false);
      setForceClearAcknowledged(false);
      setPreviewEnvelope(next);
    }
  };

  const returnToRequest = () => {
    setRetainedResetAcknowledged(false);
    setForceClearAcknowledged(false);
    setPreviewEnvelope(null);
  };

  return (
    <div className="gwOverlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="gwModal plcControllerModal plcControllerReviewModal" role="dialog" aria-modal="true" aria-label={`${label} ${controller.name}`}>
        <div className="gwModalHeader">
          <div>
            <div className="gwModalTitle">Review {label.toLocaleLowerCase()} for {controller.name}</div>
            <div className="gwModalSubtitle">Preview first, inspect the exact public change, then confirm it yourself.</div>
          </div>
          <button type="button" className="gwModalClose" onClick={onClose} aria-label="Close"><img src={xmarkIcon} alt="" className="gwIcon" /></button>
        </div>
        <div className="gwModalBody gwFormStack">
          {!proposal && (
            <>
              <div className="gwNotice isSoft">
                <strong>No command has been queued</strong>
                <span>Preview checks the current controller version, target, applications, evidence, and lifecycle transition. It does not change desired state or plant I/O.</span>
              </div>
              <div className="gwField">
                <label className="gwLabel">Reason for {label.toLocaleLowerCase()}</label>
                <textarea
                  className="gwTextarea"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  maxLength={500}
                  placeholder={`Why ${controller.name} should ${action}`}
                  autoFocus
                />
              </div>
              {action === "restart" && (
                <fieldset className="gwField plcControllerRetainedChoice">
                  <legend className="gwLabel">Retained values after restart</legend>
                  <div className="gwRadioGroup">
                    <label className="gwRadio">
                      <input
                        type="radio"
                        name="plc-controller-retained-disposition"
                        value="preserve"
                        checked={retainedDisposition === "preserve"}
                        onChange={() => setRetainedDisposition("preserve")}
                      />
                      <span><strong>Preserve</strong><small>Carry the exact durable project RETAIN checkpoint into the replacement generation.</small></span>
                    </label>
                    <label className="gwRadio">
                      <input
                        type="radio"
                        name="plc-controller-retained-disposition"
                        value="reset"
                        checked={retainedDisposition === "reset"}
                        onChange={() => setRetainedDisposition("reset")}
                      />
                      <span><strong>Reset</strong><small>Start every retained variable from its published initial value. This requires a separate acknowledgement after preview.</small></span>
                    </label>
                  </div>
                </fieldset>
              )}
              {action === "restart" && (
                <fieldset className="gwField plcControllerRetainedChoice">
                  <legend className="gwLabel">Active forces at restart</legend>
                  <div className="gwRadioGroup">
                    <label className="gwRadio">
                      <input
                        type="radio"
                        name="plc-controller-force-disposition"
                        value="reject_if_present"
                        checked={forceDisposition === "reject_if_present"}
                        onChange={() => setForceDisposition("reject_if_present")}
                      />
                      <span><strong>Reject if present</strong><small>Keep every active force intact and fail the restart if the source generation has any.</small></span>
                    </label>
                    <label className="gwRadio">
                      <input
                        type="radio"
                        name="plc-controller-force-disposition"
                        value="clear"
                        checked={forceDisposition === "clear"}
                        onChange={() => setForceDisposition("clear")}
                      />
                      <span><strong>Clear active forces</strong><small>Remove active forces during the reviewed replacement. This requires a separate acknowledgement after preview.</small></span>
                    </label>
                  </div>
                </fieldset>
              )}
            </>
          )}
          {proposal && (
            <div className="plcControllerProposal" data-gw-card="plc_controller_lifecycle_proposal">
              <div className={expired ? "gwAlert" : "gwNotice isSoft"}>
                <strong>{expired ? "Review expired" : "Human confirmation required"}</strong>
                <span>{expired ? "Go back and create a fresh preview before confirming." : "Confirmation queues only this digest-bound lifecycle command."}</span>
                <span>Execution state is separate from physical output-delivery and safety-function evidence.</span>
              </div>
              <div className="plcControllerProposalFacts">
                <div><span>Controller</span><strong>{controller.name}</strong></div>
                <div><span>Runtime</span><strong>{controller.target?.name || "Unavailable runtime"}</strong></div>
                <div><span>Target kind</span><strong>{controllerStateLabel(proposal.target_kind)}</strong></div>
                <div><span>Expires</span><strong>{timeLabel(proposal.expires_at)}</strong></div>
                {proposal.command_action === "restart" && <div><span>Retained values</span><strong>{proposal.retained_disposition === "reset" ? "Reset to published initial values" : "Preserve durable checkpoint"}</strong></div>}
                {proposal.command_action === "restart" && <div><span>Active forces</span><strong>{proposal.force_disposition === "clear" ? "Clear during replacement" : "Reject restart if any are present"}</strong></div>}
                <div><span>Proposal digest</span><code>{proposal.proposal_sha256}</code></div>
                <div><span>Reason</span><strong>{reason}</strong></div>
              </div>
              <div className="plcControllerProposalChanges" aria-label="Lifecycle change review">
                {diff.map((change) => (
                  <div key={change.label}><strong>{change.label}</strong><span className="plcChangeFlow"><span>{change.before}</span><img src={chevronIcon} alt="" aria-hidden="true" className="gwIcon" /><span>{change.after}</span></span></div>
                ))}
              </div>
              <div className="plcControllerAssignments">
                <div>
                  <span>Reviewed applications</span>
                  <strong>{proposal.applications.length ? proposal.applications.map((application) => `${application.name} v${application.published_revision}`).join(", ") : "None"}</strong>
                </div>
                {proposal.policy && (
                  <div><span>CPU / memory ceiling</span><strong>{proposal.policy.cpu_quota_millicores} millicores · {formatControllerBytes(proposal.policy.memory_max_bytes)}</strong></div>
                )}
              </div>
              {proposal.warnings.map((warning) => <div className="gwHint" key={warning}>{warning}</div>)}
              {retainedResetRequiresAcknowledgement && (
                <label className="gwCheckboxRow">
                  <input
                    type="checkbox"
                    checked={retainedResetAcknowledged}
                    onChange={(event) => setRetainedResetAcknowledged(event.target.checked)}
                  />
                  <span>I understand this reviewed restart discards the previous generation's retained checkpoint and uses the published initial values.</span>
                </label>
              )}
              {forceClearRequiresAcknowledgement && (
                <label className="gwCheckboxRow">
                  <input
                    type="checkbox"
                    checked={forceClearAcknowledged}
                    onChange={(event) => setForceClearAcknowledged(event.target.checked)}
                  />
                  <span>I understand this reviewed restart clears every active force on the source generation before replacement.</span>
                </label>
              )}
            </div>
          )}
        </div>
        <div className="gwModalFooter">
          <button type="button" className="gwGhostBtn" onClick={proposal ? returnToRequest : onClose} disabled={busy}>{proposal ? "Back" : "Cancel"}</button>
          {!proposal && <button type="button" className="gwPrimaryBtn" onClick={preview} disabled={busy || !canPreview}>{busy ? "Checking…" : `Preview ${label.toLocaleLowerCase()}`}</button>}
          {proposal && <button type="button" className={["stop", "restart"].includes(action) ? "gwDangerBtn" : "gwPrimaryBtn"} onClick={() => onConfirm(action, proposal, retainedResetAcknowledged, forceClearAcknowledged)} disabled={busy || expired || (retainedResetRequiresAcknowledgement && !retainedResetAcknowledged) || (forceClearRequiresAcknowledgement && !forceClearAcknowledged)}>{busy ? "Queueing…" : `Confirm ${label.toLocaleLowerCase()}`}</button>}
        </div>
      </div>
    </div>
  );
}

const unavailableOutputDelivery = Object.freeze({
  available: false,
  loading: false,
  message: "Physical-output delivery status is unavailable.",
  counts: { total: 0, pending: 0, leased: 0, accepted: 0, shown: 0, truncated: false },
  stages: {
    desired_durable: false,
    writer_accepted: false,
    device_observed_available: false,
    device_observed: false,
  },
  bindings: [],
});

const loadingOutputDelivery = Object.freeze({
  ...unavailableOutputDelivery,
  loading: true,
  message: "Checking physical-output delivery status.",
});

const outputDeliveryRefreshMilliseconds = 5000;
const outputDeliveryReadConcurrency = 4;
const outputDeliveryRequestTimeoutMilliseconds = 4000;

function publicOutputDeliveryProjection(payload) {
  const counts = payload?.counts;
  const stages = payload?.stages;
  const bindings = payload?.bindings;
  const available = payload?.available === true;
  const countValues = [counts?.total, counts?.pending, counts?.leased, counts?.accepted, counts?.shown];
  const valid = payload?.schema === "controlseat.plc.controller-output-delivery" && payload?.version === 1 &&
    typeof payload?.available === "boolean" && Array.isArray(bindings) &&
    countValues.every((value) => Number.isSafeInteger(value) && value >= 0) && counts.total <= 4096 &&
    counts.pending + counts.leased + counts.accepted === counts.total &&
    counts.shown === bindings.length && counts.shown <= 200 && counts.shown <= counts.total &&
    counts.truncated === (counts.total > counts.shown) &&
    stages && typeof stages.desired_durable === "boolean" && typeof stages.writer_accepted === "boolean" &&
    stages.device_observed_available === false && stages.device_observed === false &&
    stages.desired_durable === (counts.total > 0) &&
    stages.writer_accepted === (counts.total > 0 && counts.accepted === counts.total) &&
    (available || (counts.total === 0 && bindings.length === 0));
  if (!valid) return unavailableOutputDelivery;
  return {
    available,
    loading: false,
    message: available
      ? "Physical-output delivery status is available."
      : "Physical-output delivery status is unavailable.",
    counts: {
      total: counts.total,
      pending: counts.pending,
      leased: counts.leased,
      accepted: counts.accepted,
      shown: counts.shown,
      truncated: counts.truncated,
    },
    stages: {
      desired_durable: stages.desired_durable,
      writer_accepted: stages.writer_accepted,
      device_observed_available: false,
      device_observed: false,
    },
    // The card needs aggregates only. Never retain per-binding values or
    // unknown fields at this UI boundary even if a future server regresses.
    bindings: [],
  };
}

async function readOutputDeliveryBatch(controllers, startIndex, isCurrent, activeRequests, onResult) {
  if (controllers.length === 0) return 0;
  const deadline = Date.now() + outputDeliveryRequestTimeoutMilliseconds;
  let claimed = 0;
  const worker = async () => {
    while (isCurrent()) {
      const remainingMilliseconds = deadline - Date.now();
      if (remainingMilliseconds <= 0) return;
      const offset = claimed;
      claimed += 1;
      if (offset >= controllers.length) return;
      const index = (startIndex + offset) % controllers.length;
      const controller = controllers[index];
      const requestController = new AbortController();
      activeRequests.add(requestController);
      let timeout;
      const deadlineRequest = new Promise((_resolve, reject) => {
        timeout = window.setTimeout(() => {
          requestController.abort();
          reject(new Error("controller output delivery request exceeded its deadline"));
        }, remainingMilliseconds);
      });
      let status = unavailableOutputDelivery;
      try {
        // Abort is advisory: a broken fetch wrapper or test double may ignore
        // its signal. The explicit race keeps the whole fleet batch bounded
        // even when the underlying read never settles.
        const payload = await Promise.race([
          getPLCControllerOutputDelivery(controller.id, 50, requestController.signal),
          deadlineRequest,
        ]);
        status = publicOutputDeliveryProjection(payload);
      } catch {
        status = unavailableOutputDelivery;
      } finally {
        window.clearTimeout(timeout);
        activeRequests.delete(requestController);
      }
      if (isCurrent()) onResult(controller.id, status);
    }
  };
  await Promise.all(Array.from(
    { length: Math.min(outputDeliveryReadConcurrency, controllers.length) },
    () => worker()
  ));
  return (startIndex + Math.min(claimed, controllers.length)) % controllers.length;
}

function ControllerOutputDeliveryFacts({ status }) {
  const delivery = status || loadingOutputDelivery;
  const total = Number(delivery?.counts?.total || 0);
  const accepted = Number(delivery?.counts?.accepted || 0);
  const available = delivery?.available === true;
  const loading = delivery?.loading === true;
  const statusTitle = loading
    ? "Checking physical-output delivery status."
    : available
      ? total > 0
        ? `${total} desired outputs are durable; the output writer has accepted ${accepted}. Device observation is unavailable from this delivery path.`
        : "No durable desired physical outputs are recorded for the current running controller generation."
      : "Physical-output delivery status is unavailable.";
  const desiredLabel = loading
    ? "Checking…"
    : available
      ? delivery.stages?.desired_durable ? `${total.toLocaleString()} durable` : "None recorded"
      : "Unavailable";
  const writerLabel = loading
    ? "Checking…"
    : available
      ? total > 0 ? `${accepted.toLocaleString()} of ${total.toLocaleString()}` : "None recorded"
      : "Unavailable";
  return (
    <>
      <div title={statusTitle}><span>Desired</span><strong>{desiredLabel}</strong></div>
      <div title={statusTitle}><span>Writer accepted</span><strong>{writerLabel}</strong></div>
      <div title="This delivery path has no independent device-observation evidence."><span>Device observed</span><strong>Unavailable</strong></div>
    </>
  );
}

function ControllerCard({ controller, outputDelivery, capability, canConfigure, onEdit, onReview, onLifecycle, onDelete, onEvents }) {
  const editable = canConfigure && controllerCanEditDraft(controller);
  const policy = controller.policy;
  if (controller.managed_legacy) {
    // Legacy shared-driverhost inventory rows have no dedicated worker, so
    // the facts grid and lifecycle actions can never apply to them — render
    // a compact row instead of a wall of "Unavailable" and disabled buttons.
    const assigned = [
      controller.programs?.length ? `Applications: ${controller.programs.map((program) => program.name).join(", ")}` : "",
      controller.connectors?.length ? `Connectors: ${controller.connectors.map((connector) => connector.name).join(", ")}` : "",
    ].filter(Boolean).join(" · ");
    return (
      <article className="plcControllerCard isLegacy" data-gw-resource="plc_controllers" data-gw-name={controller.name}>
        <div className="plcControllerCardHeader">
          <div>
            <strong>{controller.name}</strong>
            <span>{controller.target?.name || "Unavailable runtime"} · Shared driverhost</span>
          </div>
          <div className="plcControllerPills">
            <span className="gwStatusPill isNeutral">Configuration only</span>
          </div>
        </div>
        <p className="plcControllerDescription">
          {assigned || "Applications and connectors on this runtime share the driverhost process; there is no isolated controller to start or stop."}
        </p>
        <div className="plcControllerActions">
          <button type="button" className="gwGhostBtn" onClick={() => onEvents(controller)}>View history</button>
        </div>
      </article>
    );
  }
  return (
    <article className="plcControllerCard" data-gw-resource="plc_controllers" data-gw-name={controller.name}>
      <div className="plcControllerCardHeader">
        <div>
          <strong>{controller.name}</strong>
          <span>{controller.target?.name || "Unavailable runtime"} · {controller.mode === "legacy_in_process" ? "Shared driverhost" : "Dedicated worker draft"}</span>
        </div>
        <div className="plcControllerPills">
          <span className={`gwStatusPill ${controller.actual?.stale ? "isWarning" : controllerStateTone(controller.actual?.state)}`}>{controllerStateLabel(controller.actual?.state)}</span>
          <span className="gwStatusPill isNeutral">{controller.capability?.executable ? "Executable" : "Configuration only"}</span>
        </div>
      </div>
      {controller.description && <p className="plcControllerDescription">{controller.description}</p>}
      <div className="plcControllerFacts">
        <div><span>Desired state</span><strong>{controllerStateLabel(controller.desired_state)}</strong></div>
        <div><span>Actual evidence</span><strong>{controller.actual?.authenticated ? (controller.actual.stale ? "Stale" : "Fresh") : "None"}</strong></div>
        <div><span>Revision / generation</span><strong>{controller.desired_revision} / {controller.desired_generation}</strong></div>
        <div><span>Applications / connectors</span><strong>{controller.programs?.length || 0} / {controller.connectors?.length || 0}</strong></div>
        {policy && <div><span>CPU ceiling</span><strong>{policy.cpu_quota_millicores} millicores</strong></div>}
        {policy && <div><span>Memory maximum</span><strong>{formatControllerBytes(policy.memory_max_bytes)}</strong></div>}
        {policy && <div><span>Host scheduling</span><strong>{policy.host_discipline ? "Commissioned request" : "Standard"}</strong></div>}
        <ControllerOutputDeliveryFacts status={outputDelivery} />
      </div>
      <div className="plcControllerAssignments">
        <div><span>Applications</span><strong>{controller.programs?.length ? controller.programs.map((program) => program.name).join(", ") : "None assigned"}</strong></div>
        <div><span>Connectors</span><strong>{controller.connectors?.length ? controller.connectors.map((connector) => `${connector.name} (${connector.kind})`).join(", ") : "None assigned"}</strong></div>
      </div>
      <div className="gwNotice isSoft">{controller.actual?.message || controller.capability?.message}</div>
      {Array.isArray(controller.warnings) && controller.warnings.map((warning) => <div className="gwHint" key={warning}>{warning}</div>)}
      <div className="plcControllerActions">
        {PLC_CONTROLLER_LIFECYCLE_ACTIONS.map((action) => {
          const availability = controllerLifecycleActionAvailability(controller, action, capability, canConfigure);
          return (
            <button
              type="button"
              className={action === "start" ? "gwPrimaryBtn" : ["stop", "restart"].includes(action) ? "gwDangerBtn" : "gwGhostBtn"}
              onClick={() => onLifecycle(controller, action)}
              disabled={!availability.enabled}
              title={availability.reason || `Review ${action}`}
              key={action}
            >
              {lifecycleActionLabel(action)}
            </button>
          );
        })}
        <button type="button" className="gwGhostBtn" onClick={() => onEvents(controller)}>View history</button>
        {!controller.managed_legacy && <button type="button" className="gwPrimaryBtn" onClick={() => onReview(controller)} disabled={!editable}>Review assignments and resources</button>}
        {!controller.managed_legacy && <button type="button" className="gwGhostBtn" onClick={() => onEdit(controller)} disabled={!editable}>Edit draft</button>}
        {!controller.managed_legacy && <button type="button" className="gwDangerBtn" onClick={() => onDelete(controller)} disabled={!editable}>Delete draft</button>}
      </div>
    </article>
  );
}


export {
  ControllerCard,
  ControllerDraftModal,
  ControllerEventsModal,
  ControllerLifecycleModal,
  DeleteControllerModal,
  DraftChangeModal,
  SupportEvidenceModal,
  lifecycleActionLabel,
  loadingOutputDelivery,
  outputDeliveryRefreshMilliseconds,
  readOutputDeliveryBatch,
};
